import { useEffect, useState, useMemo, useCallback } from 'react' // Added useCallback
import StandardPageWrapper from 'src/components/common/StandardPageWrapper'
import Carousel from 'src/pages/main/storyTalk/components/Carousel'
import PropTypes from 'prop-types'
import { getChatList } from 'src/services/get/getChat'
import getCharacters from 'src/services/get/getCharacters'
import { useAuthState } from 'src/providers/AuthProviders'
import useConversationListChannel from 'src/services/common/useConversationListChannel'
import TipsModal from 'src/components/common/TipsModal'
import { characterSlide } from 'src/constants/character_slide'
import { getMessages as processMessagesForDisplay } from 'src/constants/message_talk'
import { hasNotif } from 'src/lib/notification'
import { groupAvatarList } from 'src/constants/characters'

const STANDARD_WRAPPER_CLASSES =
  'carousel-standard-wrapper flex flex-col overflow-x-clip h-[calc(100dvh-230px)] fixed top-[160px]'

export const StoryTalk = ({ setStoryTalkNotification }) => {
  const { user } = useAuthState()
  const [activeGroup, setActiveGroup] = useState(0)
  const currentGroup = useMemo(() => groupAvatarList[activeGroup], [activeGroup])

  const [messages, setMessages] = useState([])
  const { message: incomingMessage } = useConversationListChannel()

  const [currentGroupCharacters, setCurrentGroupCharacters] = useState([])

  const stableSetStoryTalkNotification = useCallback(setStoryTalkNotification, [
    setStoryTalkNotification
  ])

  const charactersWithUnreadStatus = useMemo(() => {
    if (!currentGroupCharacters || !currentGroupCharacters.length) {
      return currentGroupCharacters || []
    }

    const unreadTotalsMap = messages.reduce((acc, conversation) => {
      if (conversation.character?.id) {
        acc[conversation.character.id] =
          (acc[conversation.character.id] || 0) + conversation.unreadTotal
      }
      return acc
    }, {})

    return currentGroupCharacters.map((characterAsset) => {
      return {
        ...characterAsset,
        unreadTotal: unreadTotalsMap[characterAsset.id] || 0
      }
    })
  }, [currentGroupCharacters, messages])

  useEffect(() => {
    const fetchInitialData = async () => {
      const [channelList, characters] = await Promise.all([
        getChatList(user.id, 'single_chat'),
        getCharacters()
      ])

      if (channelList && characters) {
        stableSetStoryTalkNotification(hasNotif(channelList))
        setMessages(processMessagesForDisplay(characters, channelList))
      } else {
        console.warn('Channel list or characters missing from fetch.')
      }
    }

    fetchInitialData()
  }, [user?.id, stableSetStoryTalkNotification])

  useEffect(() => {
    if (!currentGroup) return
    const processedCharacters = characterSlide(currentGroup.characters)
    if (processedCharacters.length > 0) {
      processedCharacters[0].recommend = true
    }
    setCurrentGroupCharacters(processedCharacters)
  }, [currentGroup])

  useEffect(() => {
    const newRealtimeMessage = incomingMessage
    if (!newRealtimeMessage?.conversation_id) {
      return
    }

    setMessages((prevMessages) => {
      const existingMessageIndex = prevMessages.findIndex(
        (msg) => msg.conversation_id === newRealtimeMessage.conversation_id
      )

      let updatedMessages

      if (existingMessageIndex >= 0) {
        updatedMessages = [...prevMessages]
        const currentConversationState = updatedMessages[existingMessageIndex]

        updatedMessages[existingMessageIndex] = {
          ...currentConversationState,
          ...newRealtimeMessage,
          unreadTotal: (currentConversationState.unreadTotal || 0) + 1
        }
      } else {
        const newConversation = {
          ...newRealtimeMessage,
          unreadTotal: 1
        }
        updatedMessages = [...prevMessages, newConversation]
      }

      stableSetStoryTalkNotification(true)
      return updatedMessages
    })
  }, [incomingMessage, stableSetStoryTalkNotification])

  return (
    <>
      <StandardPageWrapper className={STANDARD_WRAPPER_CLASSES}>
        <div className="w-5/6 mx-auto flex flex-row justify-center items-center mb-4 z-[9999]">
          {groupAvatarList.map((item) => (
            <div
              key={item.id}
              className="w-1/5 mr-2 cursor-pointer transform transition-all duration-300 hover:scale-105"
              onClick={() => setActiveGroup(item.id)}
            >
              <img
                src={item.id === activeGroup ? item.active : item.deactive}
                alt={item.name}
                className="transition-opacity duration-300 ease-in-out object-contain h-full w-full"
              />
            </div>
          ))}
        </div>
        <Carousel charactersToDisplay={charactersWithUnreadStatus} activeGroup={activeGroup}/>
        <TipsModal type="realtalk" />
      </StandardPageWrapper>
    </>
  )
}

StoryTalk.propTypes = {
  setStoryTalkNotification: PropTypes.func.isRequired
}
